import React, { useEffect } from "react"
import { css } from "@emotion/react"
import { Redirect, Switch, useHistory } from "react-router-dom"
import { useAuth, UserId } from "@ncs/ncs-api"
import { ClientUpdater, Toaster } from "@ncs/web-legos"
import {
	ContentWrapperEcomm,
	HelpFab,
	Route,
	SiteHeaderEcomm,
	NotFoundPage,
} from "~/shared-components"
import SiteFooter from "./shared-components/site-footer/SiteFooter"
import { ShopEcomm } from "~/views"
import { CheckoutEcommPage } from "./views/shop/checkout/CheckoutEcommPage"
import { CartSummary } from "./views/shop/cart-summary/CartSummary"
import { PartSearchResults } from "./views/shop/part-search-results/PartSearchResults"
import { PartDetail } from "./views/shop/part-detail/PartDetail"
import { useAnalytics } from "./hooks/useAnalytics"
import { ClientOnly } from "./components/ClientOnly"
import { AccountPage } from "~/views/account/AccountPage"
import { BlueWaveWelcome } from "~/views/blue-wave-welcome/BlueWaveWelcome"
import { ConnectedSiteDetail } from "~/views/connectivity/connected-site-detail"
import { ConnectedSites } from "~/views/connectivity/connected-sites"
import { CreateAccount } from "~/views/auth/create-account"
import { Dashboard } from "~/views/dashboard/Dashboard"
import { ForgotPassword as ForgotPasswordSync } from "~/views/auth/forgot-password/ForgotPassword"
import { Login } from "~/views/auth/login"
import { Logout } from "~/views/auth/logout/Logout"
import { PunchOut } from "~/views/punch-out"
import { PunchoutOrderFinished } from "~/views/shop/punchout-order-finished"
import { MyEquipment } from "~/views/my-equipment"
import { QuickOrder } from "~/views/shop/quick-order/QuickOrder"
import { ResetPassword } from "~/views/auth/reset-password"
import { ServiceRequests } from "~/views/service-requests"

const App: React.FC = () => {
	const history = useHistory()

	const loginWallAllowAnonymous = true
	const { identify, trackCustomerPortalNavigation, fetchExperiments, startExperiment } =
		useAnalytics()

	useEffect(() => {
		// Only run on client side
		if (typeof window !== "undefined" && history) {
			const unlisten = history.listen((location) => {
				trackCustomerPortalNavigation(location.pathname)
			})

			return () => {
				unlisten()
			}
		}
		// Return undefined for server side
		return undefined
	}, [history, trackCustomerPortalNavigation])

	const { user, punchOutSessionId, initialized } = useAuth()

	const userData = user ? JSON.stringify(user) : null

	useEffect(() => {
		// Only run on client side
		if (typeof window !== "undefined") {
			startExperiment()
		}
	}, [startExperiment])

	useEffect(() => {
		// Only run on client side
		if (typeof window !== "undefined") {
			if (userData && initialized) {
				const userDataParsed = JSON.parse(userData)
				identify({
					id: userDataParsed.id,
					email: userDataParsed.email,
					name: userDataParsed.name,
					punchOutSessionId: punchOutSessionId ?? null,
				})
			}
			fetchExperiments()
		}
	}, [userData, punchOutSessionId, initialized, identify, fetchExperiments])

	return (
		<div css={appContainer}>
			<ClientOnly>
				<ClientUpdater />
			</ClientOnly>
			<SiteHeaderEcomm />
			<main id="app-main" css={main}>
				<ContentWrapperEcomm>
					<Switch>
						{/* For now, the homepage / dashboard will just redirect to the shop. */}
						<Redirect exact from="/" to="/shop" />

						{/* Authentication routes */}
						<Route
							allowAnonymous
							redirectIds={UserId.DrivenBrands}
							path="/auth/forgot-password"
							component={ForgotPasswordSync}
						/>
						<Route
							allowAnonymous
							redirectIds={UserId.DrivenBrands}
							path="/auth/reset-password/:token"
							component={ResetPassword}
						/>
						<Route
							allowAnonymous
							redirectIfAuthenticated
							exact
							path="/auth/login"
							component={Login}
						/>
						<Route allowAnonymous exact path="/auth/logout" component={Logout} />
						<Route
							allowAnonymous
							redirectIfAuthenticated
							exact
							path="/auth/create-account"
							component={CreateAccount}
						/>

						{/* Punchout pages */}
						<Route allowAnonymous exact path="/punch-out" component={PunchOut} />
						<Route
							allowAnonymous
							exact
							path="/shop/punchout-order-finished"
							component={PunchoutOrderFinished}
						/>

						{/* BlueWave pages */}
						<Route exact path="/blue-wave" component={BlueWaveWelcome} />

						{/* The rest of the website */}
						<Route allowAnonymous={true} path="/dashboard" component={Dashboard} />
						<Route
							allowAnonymous={loginWallAllowAnonymous}
							exact
							path="/shop"
							component={ShopEcomm}
						/>

						<Route
							allowAnonymous={loginWallAllowAnonymous}
							exact
							path="/shop/search"
							component={PartSearchResults}
						/>

						<Route
							allowAnonymous={loginWallAllowAnonymous}
							exact
							path="/shop/parts/:partId"
							component={PartDetail}
						/>

						<Route
							allowAnonymous={loginWallAllowAnonymous}
							exact
							path="/shop/cart-summary"
							component={CartSummary}
						/>

						<Route exact path="/shop/checkout" component={CheckoutEcommPage} />
						<Route exact path="/shop/quick-order" component={QuickOrder} />
						<Route exact path="/connectivity" component={ConnectedSites} />
						<Route
							exact
							path="/connectivity/:siteId"
							component={ConnectedSiteDetail}
						/>
						<Route
							path="/service-requests"
							redirectIds={UserId.DrivenBrands}
							component={ServiceRequests}
						/>
						<Route
							path="/my-equipment"
							redirectIds={UserId.DrivenBrands}
							component={MyEquipment}
						/>
						<Route
							path="/account"
							redirectIds={UserId.DrivenBrands}
							component={AccountPage}
						/>
						<Route component={NotFoundPage} />
					</Switch>
				</ContentWrapperEcomm>
			</main>
			<SiteFooter />
			<Toaster />
			<HelpFab />
		</div>
	)
}

export default App

const appContainer = css`
	display: flex;
	flex-direction: column;
	min-height: 100vh;
`

const main = css`
	flex: 1;
	background-color: #f9fafb;
	padding-bottom: 8rem;
`
